import './QuestionForm.scss';
import { useTranslator } from '@Localization';
import { Button, IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_FieldError } from '../../../../../../design/errors/fieldError/S_FieldError';
import { S_Checkbox } from '../../../../../../design/inputs/checkbox/S_Checkbox';
import { TrashIcon } from '../../../../../../design/static/icons/TrashIcon';
import { ValidatedMultiSelectInput } from '../../../../../components/ValidatedMultiSelectInput';
import { ValidatedSelectInput } from '../../../../../components/ValidatedSelectInput';
import { ValidatedTextInput } from '../../../../../components/ValidatedTextInput';
import { InputStatus } from '../../../../../models/error.model';
import { QuestionType } from '../../../../annotation/question.model';
import { IAnnotationType, MAX_TEXT_LENGTH } from '../projectDetailsTools.models';

export type IAnswerModel = {
  text: string;
  id: string;
  status: InputStatus;
};

interface IQuestionFormProps {
  id: string;
  questionType: QuestionType;
  questionText: string;
  answers: IAnswerModel[];
  isRequired: boolean;
  questionTextStatus: InputStatus;
  answersStatus: InputStatus;
  isValid: boolean;
  isAttribute: boolean;
  isInUse: boolean;
  isReadonly: boolean;
  annotationTypes: IAnnotationType[];
  questionTypes: QuestionType[];
  selectedQuestionTypes: QuestionType[];
  scopes: string[];
  scopesStatus: InputStatus;
  onQuestionTypeChange: (questionType: QuestionType) => void;
  onQuestionTextChange: (text: string) => void;
  onRequiredChange: () => void;
  onAnswerAdd: () => void;
  onAnswerChange: (answer: string, id: string) => void;
  onAnswerRemove: (id: string) => void;
  onScopeChange: (scopes: string[] | null) => void;
}

export const QuestionForm = observer((props: IQuestionFormProps): ReactElement => {
  const { t } = useTranslator();

  const renderAnswers = (): ReactElement => {
    return (
      <>
        {props.answers.map((v, i) => (
          <div
            className='single-answer'
            key={i}
          >
            <div className='field-label'>
              {t('answer')} {i + 1}
            </div>
            <div className='field-controls'>
              <ValidatedTextInput
                type='text'
                value={v.text}
                isValid={v.status.isValid}
                disabled={props.isReadonly}
                feedbacks={v.status.errorCodes}
                onChange={(a): void => {
                  props.onAnswerChange(a, v.id);
                }}
                placeholder={t('type_answer_here')}
                maxLength={MAX_TEXT_LENGTH}
                isRequired
              />
              {!props.isReadonly && (
                <IconButton
                  size='sm'
                  onClick={(): void => {
                    props.onAnswerRemove(v.id);
                  }}
                >
                  <TrashIcon />
                </IconButton>
              )}
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderQuestionBody = (): ReactElement | null => {
    switch (props.questionType) {
      case QuestionType.MULTISELECT:
      case QuestionType.ALTERNATIVE:
        return (
          <>
            <div className='answers'>{renderAnswers()}</div>
            {!props.isReadonly && (
              <div className='add-answer'>
                <Button onClick={props.onAnswerAdd}>+ {t('add_new_option')}</Button>
                {props.answersStatus.isValid === false && <S_FieldError feedbacks={props.answersStatus.errorCodes} />}
              </div>
            )}
          </>
        );
      case QuestionType.TRUE_FALSE:
      case QuestionType.OPEN:
      case QuestionType.NUMBER:
      case QuestionType.STATUS_GOOD_BAD:
      case QuestionType.SET_TRAIN_TEST:
      default:
        return null;
    }
  };

  const isOptionDisabled = (option: QuestionType): boolean => {
    return (
      (option === QuestionType.STATUS_GOOD_BAD && props.selectedQuestionTypes.some((qt) => qt === QuestionType.STATUS_GOOD_BAD)) ||
      (option === QuestionType.SET_TRAIN_TEST && props.selectedQuestionTypes.some((qt) => qt === QuestionType.SET_TRAIN_TEST))
    );
  };

  const availableAnnotationTypes = props.annotationTypes.map((x) => ({ value: x.id, label: x.name }));

  const questionTypesOptions = props.questionTypes.map((x) => ({
    value: x,
    label: t(`${x}_question_type`),
  }));

  return (
    <div className='question-form'>
      <div>
        <ValidatedTextInput
          isRequired
          labelText='Name'
          type='text'
          id='question-input'
          value={props.questionText}
          disabled={props.isReadonly}
          feedbacks={props.questionTextStatus.errorCodes}
          isValid={props.questionTextStatus.isValid}
          onChange={(a): void => {
            props.onQuestionTextChange(a);
          }}
          placeholder={t('type_question_here')}
          maxLength={MAX_TEXT_LENGTH}
        />
      </div>
      <div>
        <ValidatedSelectInput
          required
          labelText='Type'
          onChange={(_, v) => {
            props.onQuestionTypeChange(v.value ?? QuestionType.TRUE_FALSE);
          }}
          id='question-type-dropdown'
          value={questionTypesOptions.find((x) => x.value === props.questionType)}
          getOptionDisabled={(o) => isOptionDisabled(o.value)}
          disabled={props.isReadonly || props.isInUse}
          options={questionTypesOptions}
        />
      </div>
      {props.isAttribute && (
        <ValidatedMultiSelectInput
          isRequired
          id='user-dropdown'
          name='select'
          value={availableAnnotationTypes.filter((x) => props.scopes.some((s) => s === x.value))}
          onChange={(_, v) => {
            props.onScopeChange(v.map((x) => x.value));
          }}
          label={t('scope')}
          isValid={props.scopesStatus.isValid}
          feedbacks={props.scopesStatus.errorCodes}
          disabled={props.isReadonly}
          options={availableAnnotationTypes}
        />
      )}
      <S_Checkbox
        className='is-required-checkbox'
        disabled={props.isReadonly}
        onChange={props.onRequiredChange}
        checked={props.isRequired}
        label={t('is_required')}
        id={props.id}
      />
      <div className='question-form-bottom-part'>{renderQuestionBody()}</div>
    </div>
  );
});
